// SQL (yönetici çalıştıracak):
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS takip_kodu varchar;
// ALTER TABLE depo ADD COLUMN IF NOT EXISTS kod_gecerli boolean DEFAULT true;

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

// ── Formatlayıcılar ──────────────────────────────────────────────────────────
// BYR-1234-ABCD
const formatTakipKodu = (val) => {
  const temiz = val.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 11);
  let r = temiz.slice(0, 3);
  if (temiz.length > 3) r += '-' + temiz.slice(3, 7);
  if (temiz.length > 7) r += '-' + temiz.slice(7, 11);
  return r;
};

// 0532 123 45 67
const formatTelefon = (val) => {
  const temiz = val.replace(/\D/g, '').slice(0, 11);
  let r = temiz.slice(0, 4);
  if (temiz.length > 4) r += ' ' + temiz.slice(4, 7);
  if (temiz.length > 7) r += ' ' + temiz.slice(7, 9);
  if (temiz.length > 9) r += ' ' + temiz.slice(9, 11);
  return r;
};

// ── WhatsApp Destek ──────────────────────────────────────────────────────────
const DESTEK_NUMARASI = '905011540401';
const whatsappAc = (mesaj) => {
  window.open(`https://wa.me/${DESTEK_NUMARASI}?text=${encodeURIComponent(mesaj)}`, '_blank');
};

// ── Yardımcılar ──────────────────────────────────────────────────────────────
const paraFormat = (t) =>
  t == null ? '—' : new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(Number(t)) + ' ₺';

const tarihFormat = (t) =>
  t ? new Date(t).toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const durumRenk = (durum) => {
  switch (durum) {
    case 'Tamamlandı':   return { bg: '#EAF3DE', renk: '#3B6D11' };
    case 'Kısmi Ödendi': return { bg: '#FEF3C7', renk: '#854F0B' };
    case 'Gecikmiş':     return { bg: '#FEE2E2', renk: '#DC2626' };
    default:             return { bg: '#f1f1f1', renk: '#555' };
  }
};

const kargoDurumRenk = (durum) => {
  switch (durum) {
    case 'Teslim Edildi': return { bg: '#EAF3DE', renk: '#3B6D11' };
    case "TIR'a Atandı":  return { bg: '#E6F1FB', renk: '#185FA5' };
    case 'Planlamada':    return { bg: '#FEF3C7', renk: '#854F0B' };
    default:              return { bg: '#F3F4F6', renk: '#6B7280' };
  }
};

// Sefer adımları (timeline)
const ADIMLAR = [
  { key: 'depo',     label: 'Depoda',   durumlar: ['Yükleme Bekliyor', 'Yüklendi', 'Yolda', 'Gümrükte', 'Dağıtımda', 'Teslim Edildi'] },
  { key: 'yuklendi', label: 'Yüklendi', durumlar: ['Yüklendi', 'Yolda', 'Gümrükte', 'Dağıtımda', 'Teslim Edildi'] },
  { key: 'yolda',    label: 'Yolda',    durumlar: ['Yolda', 'Gümrükte', 'Dağıtımda', 'Teslim Edildi'] },
  { key: 'gumruk',   label: 'Gümrük',   durumlar: ['Gümrükte', 'Dağıtımda', 'Teslim Edildi'] },
  { key: 'dagitim',  label: 'Dağıtım',  durumlar: ['Dağıtımda', 'Teslim Edildi'] },
  { key: 'teslim',   label: 'Teslim',   durumlar: ['Teslim Edildi'] },
];

// ── Ana bileşen ──────────────────────────────────────────────────────────────
function MusteriTakip() {
  const [mod, setMod]                     = useState('kod');   // 'kod' | 'telefon'
  const [girdi, setGirdi]                 = useState('');
  const [sorgulaniyor, setSorgulaniyor]   = useState(false);

  // Kod modu sonucu
  const [sonuc, setSonuc] = useState(null);
  // null | 'bulunamadi' | 'teslim' | { kargo, musteri, sefer, odemeler }

  // Telefon modu sonucu
  const [telSonuc, setTelSonuc] = useState(null);

  // Destek popup
  const [destekAcik, setDestekAcik] = useState(false);
  const destekRef = useRef(null);
  // null | 'bulunamadi' | { musteri, kargolar }

  useEffect(() => {
    if (!destekAcik) return;
    const kapat = (e) => { if (destekRef.current && !destekRef.current.contains(e.target)) setDestekAcik(false); };
    document.addEventListener('mousedown', kapat);
    return () => document.removeEventListener('mousedown', kapat);
  }, [destekAcik]);

  // ── Sorgula: Kargo Takip Kodu ────────────────────────────────────────────
  const sorgulaKod = async (kodParam) => {
    const temiz = (kodParam ?? girdi).trim().toUpperCase();
    if (!temiz) return;
    setSorgulaniyor(true);
    setSonuc(null);

    // 1) depo tablosunda takip_kodu ara
    const { data: depoArr } = await supabase
      .from('depo')
      .select('*, musteriler(id, ad, sehir, telefon)')
      .eq('takip_kodu', temiz)
      .limit(1);

    const kargo = depoArr?.[0];

    if (!kargo) {
      setSonuc('bulunamadi');
      setSorgulaniyor(false);
      return;
    }

    if (kargo.kod_gecerli === false) {
      setSonuc('teslim');
      setSorgulaniyor(false);
      return;
    }

    // 2) Sefer bilgisi
    let sefer = null;
    if (kargo.sefer_id) {
      const { data: seferArr } = await supabase
        .from('seferler')
        .select('*')
        .eq('id', kargo.sefer_id)
        .limit(1);
      sefer = seferArr?.[0] || null;
    }

    // 3) Ödemeler (musteri_id ile)
    let odemeler = [];
    if (kargo.musteri_id) {
      const { data: odArr } = await supabase
        .from('odemeler')
        .select('*')
        .eq('musteri_id', kargo.musteri_id)
        .order('created_at', { ascending: false });
      odemeler = odArr || [];
    }

    setSonuc({ kargo, musteri: kargo.musteriler, sefer, odemeler });
    setSorgulaniyor(false);
  };

  // ── Sorgula: Telefon ─────────────────────────────────────────────────────
  const sorgulaTel = async () => {
    const temizle = (tel) => (tel || '').replace(/\D/g, '');
    const girilenTemiz = temizle(girdi);
    if (!girilenTemiz) return;
    setSorgulaniyor(true);
    setTelSonuc(null);

    // 1) Tüm müşterileri çek, JS tarafında rakam bazlı eşleştir
    const { data: musteriArr } = await supabase
      .from('musteriler')
      .select('id, ad, sehir, telefon');

    const musteri = (musteriArr || []).find(m => temizle(m.telefon) === girilenTemiz);

    if (!musteri) {
      setTelSonuc('bulunamadi');
      setSorgulaniyor(false);
      return;
    }

    // 2) Aktif kargolarını çek
    const { data: kargolar } = await supabase
      .from('depo')
      .select('id, urun, miktar, hedef_ulke, durum, takip_kodu, sefer_id, created_at, seferler(sefer_no, guzergah, durum)')
      .eq('musteri_id', musteri.id)
      .order('created_at', { ascending: false });

    setTelSonuc({ musteri, kargolar: kargolar || [] });
    setSorgulaniyor(false);
  };

  const sorgula = () => {
    if (mod === 'kod') sorgulaKod();
    else sorgulaTel();
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') sorgula(); };

  const detayGor = (kargo) => {
    setMod('kod');
    setGirdi(kargo.takip_kodu || '');
    setSonuc(null);
    setTelSonuc(null);
    sorgulaKod(kargo.takip_kodu);
  };

  const modDegistir = (yeniMod) => {
    setMod(yeniMod);
    setGirdi('');
    setSonuc(null);
    setTelSonuc(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#F1F5F9', fontFamily: 'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#0D3B7A', color: '#fff', padding: '20px 24px', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
        <img src="/logo.png" alt="BYR SWORD" style={{ width: '160px', objectFit: 'contain', borderRadius: '8px' }} />
        <div style={{ fontSize: '16px', fontWeight: '600', marginTop: '8px', color: '#E0F2FE' }}>Kargo Takip</div>
      </div>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Mod seçici */}
        <div style={{ display: 'flex', background: '#fff', borderRadius: '12px', padding: '4px', marginBottom: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {[
            { key: 'kod',     label: '🔑 Takip Koduyla Sorgula' },
            { key: 'telefon', label: '📞 Telefonla Sorgula' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => modDegistir(tab.key)}
              style={{
                flex: 1, padding: '11px 8px',
                background: mod === tab.key ? '#0D3B7A' : 'transparent',
                color: mod === tab.key ? '#fff' : '#6B7280',
                border: 'none', borderRadius: '9px', cursor: 'pointer',
                fontSize: '13px', fontWeight: mod === tab.key ? '700' : '500',
                transition: 'all 0.2s',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Arama kutusu */}
        <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: '20px' }}>
          <div style={{ fontSize: '13px', color: '#555', marginBottom: '10px', textAlign: 'center' }}>
            {mod === 'kod'
              ? 'Kargo takip kodunuzu girerek durumu öğrenin.'
              : 'Kayıtlı telefon numaranızla kargolarınızı listeleyin.'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              value={girdi}
              onChange={e => setGirdi(mod === 'kod' ? formatTakipKodu(e.target.value) : formatTelefon(e.target.value))}
              onKeyDown={handleKeyDown}
              placeholder={mod === 'kod' ? 'BYR-2847-XK9M' : 'Örn: 05321234567'}
              maxLength={mod === 'kod' ? 14 : 20}
              style={{
                flex: 1, padding: '13px 16px',
                border: '2px solid #E2E8F0', borderRadius: '10px',
                fontSize: '16px', fontFamily: mod === 'kod' ? 'monospace' : 'inherit',
                fontWeight: '600', letterSpacing: mod === 'kod' ? '1px' : 'normal',
                outline: 'none', boxSizing: 'border-box',
                textTransform: mod === 'kod' ? 'uppercase' : 'none',
              }}
            />
            <button
              onClick={sorgula}
              disabled={sorgulaniyor || !girdi.trim()}
              style={{
                background: '#0D3B7A', color: '#fff', border: 'none',
                padding: '13px 22px', borderRadius: '10px', fontSize: '14px',
                fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
                opacity: sorgulaniyor || !girdi.trim() ? 0.6 : 1,
              }}
            >
              {sorgulaniyor ? '...' : 'Sorgula'}
            </button>
          </div>
        </div>

        {/* ── KOD MODU SONUÇLARI ── */}
        {mod === 'kod' && sonuc === 'bulunamadi' && (
          <BildirimKutu renk="kirmizi"
            baslik="Geçersiz Takip Kodu"
            mesaj="Girdiğiniz takip kodu bulunamadı. Lütfen kodu doğru girdiğinizden emin olun." />
        )}

        {mod === 'kod' && sonuc === 'teslim' && (
          <BildirimKutu renk="sari"
            baslik="Bu Kargo Teslim Edilmiştir"
            mesaj="Bu kargoya ait teslimat tamamlanmıştır. Yeni gönderi için firmamızla iletişime geçebilirsiniz.">
            <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <a href="https://byrsword.com" target="_blank" rel="noreferrer"
                style={{ color: '#854F0B', fontWeight: '600', fontSize: '13px', textDecoration: 'none', background: '#FEF3C7', padding: '6px 14px', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                🌐 byrsword.com
              </a>
              <a href="tel:+905000000000"
                style={{ color: '#854F0B', fontWeight: '600', fontSize: '13px', textDecoration: 'none', background: '#FEF3C7', padding: '6px 14px', borderRadius: '8px', border: '1px solid #FDE68A' }}>
                📞 Bizi Ara
              </a>
            </div>
          </BildirimKutu>
        )}

        {mod === 'kod' && sonuc && typeof sonuc === 'object' && (
          <KargoDetay sonuc={sonuc} />
        )}

        {/* ── TELEFON MODU SONUÇLARI ── */}
        {mod === 'telefon' && telSonuc === 'bulunamadi' && (
          <BildirimKutu renk="kirmizi"
            baslik="Müşteri Bulunamadı"
            mesaj="Bu telefon numarasıyla kayıtlı bir müşteri bulunamadı." />
        )}

        {mod === 'telefon' && telSonuc && typeof telSonuc === 'object' && (
          <TelefonSonuc telSonuc={telSonuc} onDetay={detayGor} />
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: '12px', color: '#9CA3AF' }}>BYR SWORD LOJİSTİK · byrsword.com</div>
        </div>
      </div>

      {/* ── Floating Destek Hattı ── */}
      <div ref={destekRef} style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 500 }}>
        {destekAcik && (
          <div style={{
            position: 'absolute', bottom: '64px', right: 0, width: '260px',
            background: '#fff', borderRadius: '14px', boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
            border: '1px solid #E2E8F0', overflow: 'hidden',
          }}>
            <div style={{ background: '#0D3B7A', padding: '12px 16px', color: '#fff', fontSize: '14px', fontWeight: '700' }}>
              💬 Destek Hattı
            </div>
            {[
              { ikon: '📦', label: 'Operasyon', mesaj: 'Merhaba, kargom hakkında bilgi almak istiyorum.' },
              { ikon: '💰', label: 'Muhasebe', mesaj: 'Merhaba, ödeme/fatura konusunda bilgi almak istiyorum.' },
              { ikon: '💵', label: 'Hızlı Fiyat Al', mesaj: 'Merhaba, nakliye fiyat teklifi almak istiyorum.' },
            ].map((item, i) => (
              <div
                key={i}
                onClick={() => { whatsappAc(item.mesaj); setDestekAcik(false); }}
                onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '14px 16px', cursor: 'pointer', fontSize: '14px',
                  borderBottom: i < 2 ? '1px solid #F1F5F9' : 'none',
                  fontWeight: '600', color: '#1a1a1a', transition: 'background 0.15s',
                }}
              >
                <span style={{ fontSize: '20px' }}>{item.ikon}</span>
                {item.label}
              </div>
            ))}
          </div>
        )}
        <button
          onClick={() => setDestekAcik(!destekAcik)}
          style={{
            width: '56px', height: '56px', borderRadius: '50%',
            background: '#25D366', color: '#fff', border: 'none',
            cursor: 'pointer', fontSize: '28px', lineHeight: 1,
            boxShadow: '0 4px 16px rgba(37,211,102,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'transform 0.2s',
            transform: destekAcik ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          {destekAcik ? '✕' : '💬'}
        </button>
      </div>
    </div>
  );
}

// ── Kargo Detay (Kod Modu) ────────────────────────────────────────────────────
function KargoDetay({ sonuc }) {
  const { kargo, musteri, sefer, odemeler } = sonuc;
  const { bg, renk } = kargoDurumRenk(kargo.durum);

  return (
    <>
      {/* Müşteri kartı */}
      <div style={{ background: '#0D3B7A', borderRadius: '14px', padding: '20px 24px', marginBottom: '14px', color: '#fff' }}>
        <div style={{ fontSize: '12px', color: '#93C5FD', marginBottom: '4px' }}>Hoş geldiniz</div>
        <div style={{ fontSize: '20px', fontWeight: '800' }}>{musteri?.ad || '—'}</div>
        {musteri?.sehir && <div style={{ fontSize: '13px', color: '#BFDBFE', marginTop: '2px' }}>📍 {musteri.sehir}</div>}
      </div>

      {/* Kargo bilgisi */}
      <SekmeBaslik baslik="📦 Kargo Bilgisi" />
      <div style={{ background: '#fff', borderRadius: '12px', padding: '18px 20px', marginBottom: '14px', border: '1px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ fontWeight: '700', fontSize: '16px', color: '#111827' }}>{kargo.urun || '—'}</div>
          <span style={{ background: bg, color: renk, padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap', marginLeft: '8px' }}>
            {kargo.durum}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {kargo.miktar && <MiniSatir label="Miktar" value={kargo.miktar} />}
          {kargo.hedef_ulke && <MiniSatir label="Hedef Ülke" value={kargo.hedef_ulke} />}
          {kargo.agirlik && <MiniSatir label="Ağırlık" value={kargo.agirlik + ' kg'} />}
          {kargo.takip_kodu && (
            <MiniSatir label="Takip Kodu" value={
              <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0D3B7A' }}>{kargo.takip_kodu}</span>
            } />
          )}
        </div>
      </div>

      {/* Sefer takibi */}
      {sefer && (
        <>
          <SekmeBaslik baslik="🚛 Sefer Takibi" />
          <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '14px', border: '1px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <div style={{ fontWeight: '800', fontSize: '16px', color: '#0D3B7A' }}>{sefer.sefer_no}</div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '2px' }}>{sefer.guzergah}</div>
              </div>
              {sefer.tahmini_varis && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '10px', color: '#9CA3AF' }}>Tahmini Varış</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{tarihFormat(sefer.tahmini_varis)}</div>
                </div>
              )}
            </div>
            {/* Timeline */}
            <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: '4px' }}>
              {ADIMLAR.map((adim, idx) => {
                const tamamlandi = adim.durumlar.includes(sefer.durum);
                const aktif = tamamlandi && (idx === ADIMLAR.length - 1 || !ADIMLAR[idx + 1].durumlar.includes(sefer.durum));
                return (
                  <React.Fragment key={adim.key}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '60px' }}>
                      <div style={{
                        width: '28px', height: '28px', borderRadius: '50%',
                        background: tamamlandi ? (aktif ? '#0D3B7A' : '#3B6D11') : '#E5E7EB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', color: '#fff', fontWeight: '700',
                        border: aktif ? '3px solid #93C5FD' : 'none',
                        flexShrink: 0,
                      }}>
                        {tamamlandi ? '✓' : ''}
                      </div>
                      <div style={{ fontSize: '9px', color: tamamlandi ? '#374151' : '#9CA3AF', marginTop: '4px', textAlign: 'center', fontWeight: aktif ? '700' : '400', lineHeight: '1.2' }}>
                        {adim.label}
                      </div>
                    </div>
                    {idx < ADIMLAR.length - 1 && (
                      <div style={{ flex: 1, height: '2px', background: ADIMLAR[idx + 1].durumlar.includes(sefer.durum) ? '#3B6D11' : '#E5E7EB', minWidth: '8px' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Ödemeler */}
      {odemeler.length > 0 && (
        <>
          <SekmeBaslik baslik="💳 Ödeme Durumum" sayi={odemeler.length} />
          {odemeler.map(o => {
            const kalan = o.toplam_tutar - (o.odenen_tutar || 0);
            const { bg: obg, renk: or } = durumRenk(o.odeme_durumu);
            return (
              <div key={o.id} style={{ background: '#fff', borderRadius: '12px', padding: '16px', marginBottom: '10px', border: '1px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a1a' }}>{o.aciklama || 'Ödeme'}</div>
                  <span style={{ background: obg, color: or, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>{o.odeme_durumu}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  <TutarKutu label="Toplam" deger={paraFormat(o.toplam_tutar)} bg="#F8FAFC" renk="#374151" />
                  <TutarKutu label="Ödenen" deger={paraFormat(o.odenen_tutar || 0)} bg="#F0FDF4" renk="#3B6D11" />
                  <TutarKutu label="Kalan" deger={paraFormat(kalan)} bg={kalan > 0 ? '#FFF1F2' : '#F0FDF4'} renk={kalan > 0 ? '#DC2626' : '#3B6D11'} />
                </div>
                {o.vade_tarihi && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#888', textAlign: 'right' }}>
                    Vade: {tarihFormat(o.vade_tarihi)}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

// ── Telefon Modu Sonucu ───────────────────────────────────────────────────────
function TelefonSonuc({ telSonuc, onDetay }) {
  const { musteri, kargolar } = telSonuc;

  const aktif = kargolar.filter(k => k.durum !== 'Teslim Edildi');

  return (
    <>
      {/* Müşteri kartı */}
      <div style={{ background: '#0D3B7A', borderRadius: '14px', padding: '18px 22px', marginBottom: '14px', color: '#fff' }}>
        <div style={{ fontSize: '12px', color: '#93C5FD', marginBottom: '3px' }}>Hoş geldiniz</div>
        <div style={{ fontSize: '20px', fontWeight: '800' }}>{musteri.ad}</div>
        {musteri.sehir && <div style={{ fontSize: '13px', color: '#BFDBFE', marginTop: '2px' }}>📍 {musteri.sehir}</div>}
      </div>

      {aktif.length === 0 ? (
        <BosKart mesaj="Bu müşteriye ait aktif kargo bulunmuyor." />
      ) : (
        <>
          <SekmeBaslik baslik="📦 Aktif Kargolar" sayi={aktif.length} />
          {aktif.map(k => <KargoKart key={k.id} k={k} onDetay={onDetay} />)}
        </>
      )}
    </>
  );
}

function KargoKart({ k, onDetay, teslimEdildi }) {
  const { bg, renk } = kargoDurumRenk(k.durum);
  return (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '16px 18px',
      marginBottom: '10px', border: '1px solid #E2E8F0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      borderLeft: teslimEdildi ? '4px solid #16A34A' : '4px solid #0D3B7A',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>{k.urun || '—'}</div>
        <span style={{ background: bg, color: renk, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap', marginLeft: '8px' }}>
          {k.durum}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', marginBottom: '10px' }}>
        {k.miktar && <MiniSatir label="Miktar" value={k.miktar} />}
        {k.hedef_ulke && <MiniSatir label="Hedef" value={k.hedef_ulke} />}
        {k.seferler?.sefer_no && <MiniSatir label="Sefer" value={k.seferler.sefer_no} />}
        {k.seferler?.guzergah && <MiniSatir label="Güzergah" value={k.seferler.guzergah} />}
        {k.takip_kodu && (
          <MiniSatir label="Takip Kodu" value={
            <span style={{ fontFamily: 'monospace', fontWeight: '700', color: '#0D3B7A', fontSize: '12px' }}>{k.takip_kodu}</span>
          } />
        )}
      </div>

      {k.takip_kodu && !teslimEdildi && (
        <button
          onClick={() => onDetay(k)}
          style={{
            width: '100%', padding: '9px',
            background: '#0D3B7A', color: '#fff',
            border: 'none', borderRadius: '8px',
            fontSize: '13px', fontWeight: '700', cursor: 'pointer',
          }}
        >
          🔍 Detay Gör
        </button>
      )}
    </div>
  );
}

// ── Küçük yardımcı bileşenler ─────────────────────────────────────────────────
function BildirimKutu({ renk, baslik, mesaj, children }) {
  const s = {
    kirmizi: { bg: '#FEF2F2', border: '#FECACA', baslikRenk: '#DC2626', metinRenk: '#7F1D1D' },
    sari:    { bg: '#FFFBEB', border: '#FDE68A', baslikRenk: '#92400E', metinRenk: '#78350F' },
  }[renk];
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
      <div style={{ fontWeight: '700', color: s.baslikRenk, marginBottom: '6px', fontSize: '15px' }}>{baslik}</div>
      <div style={{ color: s.metinRenk, fontSize: '13px', lineHeight: '1.6' }}>{mesaj}</div>
      {children}
    </div>
  );
}

function SekmeBaslik({ baslik, sayi }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', marginTop: '6px' }}>
      <div style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>{baslik}</div>
      {sayi != null && (
        <div style={{ background: '#E0E7FF', color: '#3730A3', padding: '1px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>{sayi}</div>
      )}
    </div>
  );
}

function BosKart({ mesaj }) {
  return (
    <div style={{ background: '#fff', borderRadius: '12px', border: '1px dashed #CBD5E1', padding: '28px', textAlign: 'center', color: '#94A3B8', fontSize: '13px', marginBottom: '10px' }}>
      {mesaj}
    </div>
  );
}

function MiniSatir({ label, value }) {
  return (
    <div>
      <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{label}: </span>
      <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>{value}</span>
    </div>
  );
}

function TutarKutu({ label, deger, bg, renk }) {
  return (
    <div style={{ textAlign: 'center', background: bg, borderRadius: '8px', padding: '8px' }}>
      <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontWeight: '700', color: renk }}>{deger}</div>
    </div>
  );
}


export default MusteriTakip;
